import {
  Badge,
  Button,
  Callout,
  Checkbox,
  Dialog,
  Icon,
  Input,
  useObsinaI18n,
} from "@obsina/design-system";
import { Info, Lock, Search } from "lucide-react";
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  deselectMailFolder,
  getMailFolderSelections,
  listMailFolders,
  selectMailFolder,
} from "./api";
import { APP_LOCALE } from "./locale";
import type { AppMessages } from "./messages";
import type { MailFolder, MailFolderSelection } from "./mail-folders";

type LoadState = "idle" | "loading" | "ready" | "error";

/**
 * A folder this size is worth a word before it is added. The threshold is a
 * display decision only — nothing downstream reads it — and it exists because
 * the live tenant's archive holds 56,113 items, which is not a number a member
 * should discover after the fact.
 */
const LARGE_FOLDER_ITEM_COUNT = 5_000;

export interface MailFolderDialogProps {
  readonly apiBase: string | null;
  readonly connectionId: string;
  readonly messages: AppMessages;
  readonly onOpenChange: (open: boolean) => void;
  readonly onSelectionChange: (count: number, reloadSummary?: boolean) => void;
  readonly open: boolean;
}

/**
 * The explicit mail-scope step.
 *
 * One decision, one list: which folders of this mailbox are read. The two the
 * plan always reads are shown in the same list as scope the member already has,
 * rather than hidden — a chooser that listed only the extras would describe a
 * two-folder scope as an empty one.
 */
export function MailFolderDialog({
  apiBase,
  connectionId,
  messages,
  onOpenChange,
  onSelectionChange,
  open,
}: MailFolderDialogProps) {
  const copy = messages.mailFolders;
  const { locale } = useObsinaI18n();
  const listHeadingId = useId();
  const [folders, setFolders] = useState<readonly MailFolder[]>([]);
  const [truncated, setTruncated] = useState(false);
  const [folderState, setFolderState] = useState<LoadState>("idle");
  const [selections, setSelections] = useState<readonly MailFolderSelection[]>(
    [],
  );
  const [selectionState, setSelectionState] = useState<LoadState>("idle");
  const [retry, setRetry] = useState(0);
  const [filter, setFilter] = useState("");
  const [pendingFolderId, setPendingFolderId] = useState<string | null>(null);
  const [mutationFailed, setMutationFailed] = useState(false);
  const [announcement, setAnnouncement] = useState("");
  const controller = useRef<AbortController | null>(null);

  useEffect(() => {
    if (open) return;
    controller.current?.abort();
    setFilter("");
    setMutationFailed(false);
    setAnnouncement("");
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (!apiBase) {
      setFolderState("error");
      setSelectionState("error");
      return;
    }
    const abort = new AbortController();
    controller.current = abort;
    setFolderState("loading");
    setSelectionState("loading");
    // Both reads are issued together: the list is meaningless without knowing
    // which of its rows are already chosen, and a member should not watch two
    // spinners resolve one after the other.
    void Promise.allSettled([
      listMailFolders(apiBase, connectionId, abort.signal),
      getMailFolderSelections(apiBase, connectionId, abort.signal),
    ]).then(([page, selected]) => {
      if (abort.signal.aborted) return;
      if (page.status === "fulfilled") {
        setFolders(page.value.folders);
        setTruncated(page.value.truncated);
        setFolderState("ready");
      } else {
        setFolderState("error");
      }
      if (selected.status === "fulfilled") {
        setSelections(selected.value);
        setSelectionState("ready");
        onSelectionChange(selected.value.length);
      } else {
        setSelectionState("error");
      }
    });
    return () => abort.abort();
  }, [apiBase, connectionId, onSelectionChange, open, retry]);

  useEffect(() => () => controller.current?.abort(), []);

  const selectedIds = useMemo(
    () => new Set(selections.map((selection) => selection.folderId)),
    [selections],
  );

  // Anchors first, then everything else in the reader's own alphabet. Without
  // the locale, Turkish letters collate by code point and the visible order is
  // wrong: I sorts away from ı and Ş lands after Z.
  const visibleFolders = useMemo(() => {
    const needle = filter.trim().toLocaleLowerCase(APP_LOCALE);
    return [...folders]
      .filter(
        (folder) =>
          needle.length === 0 ||
          folder.path.toLocaleLowerCase(APP_LOCALE).includes(needle),
      )
      .sort((left, right) => {
        if ((left.anchor !== null) !== (right.anchor !== null)) {
          return left.anchor !== null ? -1 : 1;
        }
        return left.path.localeCompare(right.path, APP_LOCALE);
      });
  }, [filter, folders]);

  const toggleFolder = useCallback(
    async (folder: MailFolder, next: boolean) => {
      if (!apiBase || pendingFolderId || selectionState !== "ready") return;
      setPendingFolderId(folder.id);
      setMutationFailed(false);
      try {
        let updated: readonly MailFolderSelection[];
        if (next) {
          if (selectedIds.has(folder.id)) return;
          const created = await selectMailFolder(
            apiBase,
            connectionId,
            folder.id,
          );
          updated = [...selections, created];
          setAnnouncement(copy.addedAnnouncement(folder.displayName));
        } else {
          if (!selectedIds.has(folder.id)) return;
          await deselectMailFolder(apiBase, connectionId, folder.id);
          updated = selections.filter(
            (selection) => selection.folderId !== folder.id,
          );
          setAnnouncement(copy.removedAnnouncement(folder.displayName));
        }
        setSelections(updated);
        onSelectionChange(updated.length, true);
      } catch {
        setMutationFailed(true);
      } finally {
        setPendingFolderId(null);
      }
    },
    [
      apiBase,
      connectionId,
      copy,
      onSelectionChange,
      pendingFolderId,
      selectedIds,
      selectionState,
      selections,
    ],
  );

  const failed = folderState === "error" || selectionState === "error";

  return (
    <Dialog
      description={copy.dialogDescription}
      footer={
        <Button onClick={() => onOpenChange(false)} variant="secondary">
          {copy.close}
        </Button>
      }
      onOpenChange={onOpenChange}
      open={open}
      size="md"
      title={copy.dialogTitle}
    >
      <div className="mail-folder-picker">
        {open && (
          <p aria-live="polite" className="obsina-sr-only" role="status">
            {announcement}
          </p>
        )}

        {mutationFailed && (
          <Callout title={copy.mutationUnavailableTitle} tone="danger">
            {copy.mutationUnavailableBody}
          </Callout>
        )}

        {failed ? (
          <Callout
            title={
              folderState === "error"
                ? copy.unavailableTitle
                : copy.selectionsUnavailableTitle
            }
            tone="danger"
          >
            <p>
              {folderState === "error"
                ? copy.unavailableBody
                : copy.selectionsUnavailableBody}
            </p>
            <Button
              onClick={() => setRetry((value) => value + 1)}
              size="sm"
              variant="secondary"
            >
              {copy.retry}
            </Button>
          </Callout>
        ) : (
          <>
            {/* One row carries the heading, the count it is about and the
                control that narrows it, so none of the three claims a band of
                its own. */}
            <div className="mail-folder-picker__head">
              <h3 id={listHeadingId}>{copy.listHeading}</h3>
              {selectionState === "ready" && selections.length > 0 && (
                <Badge tone="accent">
                  {copy.selectedCount(selections.length)}
                </Badge>
              )}
              {/* No visible label: the heading beside it already names what
                  the control filters, and a second word for the same thing is
                  the stacked-heading this surface's density rule forbids. */}
              <Input
                aria-label={copy.filterLabel}
                fieldClassName="mail-folder-picker__filter"
                maxLength={200}
                onChange={(event) => setFilter(event.target.value)}
                placeholder={copy.filterPlaceholder}
                size="sm"
                startAdornment={<Icon icon={Search} size={15} />}
                type="search"
                value={filter}
              />
            </div>

            {folderState !== "ready" ? (
              <p className="mail-folder-picker__state" role="status">
                {copy.loading}
              </p>
            ) : visibleFolders.length === 0 ? (
              <p className="mail-folder-picker__state">
                {folders.length === 0 ? copy.empty : copy.filterEmpty}
              </p>
            ) : (
              <ul aria-labelledby={listHeadingId} className="mail-folder-list">
                {visibleFolders.map((folder) => {
                  const checked =
                    folder.anchor !== null || selectedIds.has(folder.id);
                  return (
                    <li
                      data-anchor={folder.anchor !== null || undefined}
                      data-selected={checked || undefined}
                      key={folder.id}
                    >
                      <Checkbox
                        aria-label={folder.path}
                        checked={checked}
                        disabled={
                          folder.anchor !== null ||
                          selectionState !== "ready" ||
                          pendingFolderId !== null
                        }
                        label={
                          <span className="mail-folder-list__copy">
                            <strong>{folder.path}</strong>
                          </span>
                        }
                        onChange={(event) =>
                          void toggleFolder(folder, event.target.checked)
                        }
                      />
                      <span className="mail-folder-list__count">
                        {copy.itemCount(folder.totalItemCount, locale)}
                      </span>
                      {folder.anchor !== null ? (
                        <span className="mail-folder-list__note">
                          <Icon icon={Lock} size={12} />
                          {copy.alwaysRead}
                        </span>
                      ) : folder.totalItemCount >= LARGE_FOLDER_ITEM_COUNT ? (
                        <span
                          className="mail-folder-list__note"
                          data-tone="caution"
                        >
                          <Icon icon={Info} size={12} />
                          {copy.largeFolder}
                        </span>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            )}

            {truncated && folderState === "ready" && (
              <p className="mail-folder-picker__truncated">
                <Icon icon={Info} size={14} />
                {copy.truncated}
              </p>
            )}
          </>
        )}
      </div>
    </Dialog>
  );
}
